import { ProcessRun } from '~/types/DTO';
import { WithWorkspaceId } from '~/types/common';
import { ProcessStatus, StopRequirementsFromControlRoom } from '~/types/resources';
import { integrationProcessApi } from '~/utils/api';

export const startProcess = async ({
  workspaceId,
  processId,
  payload,
}: { processId: string; payload?: string } & WithWorkspaceId) =>
  integrationProcessApi.post<ProcessRun>(
    `/workspaces/${workspaceId}/processes/${processId}/runs`,
    payload,
  );

export const startProcessWithQuery = async ({
  workspaceId,
  processId,
  query,
}: { processId: string; query: string } & WithWorkspaceId) =>
  integrationProcessApi.post<ProcessRun>(
    `/workspaces/${workspaceId}/processes/${processId}/run-request`,
    {
      type: 'query',
      query,
    },
  );

export const startDefaultProcessRun = async ({
  workspaceId,
  processId,
}: { processId: string } & WithWorkspaceId) =>
  integrationProcessApi.post<ProcessRun>(
    `/workspaces/${workspaceId}/processes/${processId}/run-request`,
    {
      type: 'default',
    },
  );

export const startStorageProcessRun = async ({
  workspaceId,
  processId,
  storages,
}: { processId: string; storages: string[] } & WithWorkspaceId) =>
  integrationProcessApi.post<ProcessRun>(
    `/workspaces/${workspaceId}/processes/${processId}/run-request`,
    {
      type: 'storages',
      storages,
    },
  );

export const startProcessWithItems = async ({
  workspaceId,
  processId,
  workItemIds,
}: { processId: string; workItemIds: string[] } & WithWorkspaceId) =>
  integrationProcessApi.post<ProcessRun>(
    `/workspaces/${workspaceId}/processes/${processId}/run-request`,
    {
      type: 'workItemIds',
      workItemIds,
    },
  );

export const listWorkspaceProcessStatuses = async ({ workspaceId }: WithWorkspaceId) =>
  integrationProcessApi.get<{ data: ProcessStatus[] }>(`/workspaces/${workspaceId}/processes`);

export const getProcessStatus = async ({
  workspaceId,
  processId,
}: { processId: string } & WithWorkspaceId) =>
  integrationProcessApi.get<ProcessStatus>(`/workspaces/${workspaceId}/processes/${processId}`);

export const resetStatus = async ({
  workspaceId,
  processId,
}: { processId: string } & WithWorkspaceId) =>
  integrationProcessApi.post<ProcessStatus>(
    `/workspaces/${workspaceId}/processes/${processId}/reset-stat`,
  );

export const stopProcessRun = async ({
  workspaceId,
  processId,
  pRunId,
  stopRequirements,
}: {
  processId: string;
  pRunId: string;
  stopRequirements: StopRequirementsFromControlRoom;
} & WithWorkspaceId) =>
  integrationProcessApi.post<{ accepted: boolean }>(
    `/workspaces/${workspaceId}/processes/${processId}/runs/${pRunId}/stop`,
    stopRequirements,
  );

export const completeProcessRun = async ({
  workspaceId,
  processId,
  pRunId,
  completeParams,
}: {
  processId: string;
  pRunId: string;
  completeParams: { reason?: string };
} & WithWorkspaceId) =>
  integrationProcessApi.post<{ accepted: boolean }>(
    `/workspaces/${workspaceId}/processes/${processId}/runs/${pRunId}/complete`,
    completeParams,
  );

export const deleteProcessRun = async ({
  workspaceId,
  processId,
  pRunId,
}: { processId: string; pRunId: string } & WithWorkspaceId) =>
  integrationProcessApi.delete<void>(
    `/workspaces/${workspaceId}/processes/${processId}/runs/${pRunId}`,
  );

type SubmitIssueParams = {
  processId: string;
  processRunId: string;
  stepRunId: string;
  description: string;
  url: string;
  userId: string;
};

export const submitStepRunIssue = async ({
  workspaceId,
  ...params
}: SubmitIssueParams & WithWorkspaceId) => {
  const { processId, processRunId, stepRunId, description, url, userId } = params;
  return integrationProcessApi.post<void>(
    `/workspaces/${workspaceId}/processes/${processId}/runs/${processRunId}/robotRuns/${stepRunId}/issues`,
    {
      description,
      url,
      userId,
    },
  );
};
